import { useState } from "react";
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from "react-query";
import cn from "classnames";
import { Title2, SubTitle3 } from "@components/new/Typo";
import { LinkButton, LinkButtonSize } from "@components/new/LinkButton";
import TextInput from "@components/TextInput/TextInput";
import Radio from "@components/new/Radio";
import { Modal, ModalSize } from "@components/new/Modal";
import { MarketplaceStoreGroup } from "@components/new/MarketplaceStoreGroup";
import { Marketplace } from "@components/new/Marketplace";
import AddWarehouseModal, { IWBInfoItem, IYMInfoItem } from "../AddWarehouseModal";
// import { ButtonType } from "@components/new/Button";
import {
  moveSellers,
  TWarehouse,
  TSellerWarehouse,
} from "@api/apiClient/fbp";
import IconPlus from '@assets/icons/outline/plus.svg?react';
import SearchIcon from "@assets/icons/outline/search.svg?react";
import styles from "./MoveModal.module.scss";

interface MoveModalProps {
  onClose: () => void;
  onSuccess: (v: string) => void;
  onError: () => void;
  warehouse: TWarehouse;
  sellerFromId: string;
  ymItems: IYMInfoItem[];
  wbItems: IWBInfoItem[];
}

const MoveModal = ({
  onClose,
  onSuccess,
  onError,
  warehouse,
  sellerFromId,
  ymItems,
  wbItems,
}: MoveModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState<TSellerWarehouse | undefined>();

  const mergeMutation = useMutation({
    mutationFn: moveSellers,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getWarehouses"] })
      queryClient.invalidateQueries({ queryKey: ["getSellerDetails", selectedWarehouse?.id ?? ""] })
      queryClient.invalidateQueries({ queryKey: ["getSellerDetails", sellerFromId] })
      onSuccess(selectedWarehouse?.name ?? "");
    },
    onError: () => onError(),
  });

  const filtredWarehouses = (warehouse?.sellerWarehouses || [])
    .filter(v => !v.isAwaitingDeletion)
    .filter(v => (v.id !== sellerFromId))
    .filter(v => (!name.trim() || (v.name.toUpperCase().indexOf(name.toUpperCase()) > -1)));

  return (
    <Modal
      isOpen={true}
      title={t('fbpPage.moveStoreModal.title')}
      size={ModalSize.M}
      onClose={onClose}
      footerButtons={[
        {
          title: t('fbpPage.moveStoreModal.cancelBtn'),
        },
        {
          title: t('fbpPage.moveStoreModal.moveBtn'),
          onClick: () => {
            mergeMutation.mutate({
              destinationSellerWarehouseId: selectedWarehouse?.id,
              wbWarehouseIds: wbItems.map(v => v.id),
              ymWarehouseIds: ymItems.map(v => v.id),
            });
            onSuccess(selectedWarehouse?.name ?? "");
          },
          disabled: !selectedWarehouse,
          loading: false,
        },
      ]}
      inModal
    >
      <div className={styles.section}>
        <div className={styles.searchRow}>
          <TextInput
            value={name}
            onChange={setName}
            placeholder={t('fbpPage.moveStoreModal.placeholder')}
            prefix={<SearchIcon />}
          />
          <LinkButton
            icon={IconPlus}
            size={LinkButtonSize.L}
            title={t("fbpPage.moveStoreModal.newLink")}
            onClick={() => {
              setIsModalOpen(true);
            }}
          />
        </div>
        <div className={styles.list}>
          {filtredWarehouses.map(v => (
            <div
              key={v.id}
              className={cn(styles.radio, v.id === selectedWarehouse?.id && styles.checked)}
              onClick={(e: any) => {
                e.stopPropagation();
                e.preventDefault();
                if (v.id !== selectedWarehouse?.id) {
                  setSelectedWarehouse(v)
                } else { setSelectedWarehouse(undefined) }
              }}
            >
              <Radio checked={v.id === selectedWarehouse?.id} />
              <div className={styles.radioTitle}>
                <MarketplaceStoreGroup>
                  {v.wbWarehouses?.length ? (<Marketplace name="Wildberries" />) : null}
                  {v.ymWarehouses?.length ? (<Marketplace name="Yandex" />) : null}
                </MarketplaceStoreGroup>
                <Title2>{v.name}</Title2>
                <SubTitle3 className={styles.counter}>
                  {t("fbpPage.moveStoreModal.stores", { count: v.ymWarehouses?.length + v.wbWarehouses?.length })}
                </SubTitle3>
              </div>
            </div>
          ))}
        </div>
      </div>

      {isModalOpen && (
        <AddWarehouseModal
          onClose={() => {
            setIsModalOpen(false);
          }}
          onSuccess={(newSellerWarehouseName: string) => {
            setIsModalOpen(false);
            onSuccess(newSellerWarehouseName);
          }}
          warehouse={warehouse}
          ymItems={ymItems}
          wbItems={wbItems}
          isNewLinkToMove
        />
      )}
    </Modal>
  )
}

export default MoveModal;
